<script setup>
import { computed, ref, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * Form container providing submit handling, validation, and optional autofocus.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Wrapping a set of form fields that need submit handling or validation
 * @see https://quasar.dev/vue-components/form Quasar q-form API
 */
defineOptions({ name: 'HrForm' });

const props = defineProps({
  /** When true, automatically focuses the first focusable field in the form on mount. */
  autofocus: {
    type: Boolean,
    default: false,
  },
  /**
   * Validate all fields even after the first error, instead of stopping at the first failure.
   */
  greedy: {
    type: Boolean,
    default: false,
  },
  /** When true, does not scroll to and focus the first error field after a failed validation. */
  noErrorFocus: {
    type: Boolean,
    default: false,
  },
  /** When true, does not focus the first field after the form is reset. */
  noResetFocus: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props forwarded to the underlying Quasar component.
   * @deprecated Only for backwards compatibility with the legacy theme — ignored when a design system theme is active.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Emitted when the form passes validation and is submitted; payload is the native submit event. */
  submit: null,
  /** Emitted when validation fails; payload is the first field component that failed. */
  validationError: null,
  /** Emitted when all fields pass validation. */
  validationSuccess: null,
});

const attrs = useAttrs();
const { hasTheme } = useTheme();
const formRef = ref(null);

const effectiveProps = computed(() => {
  const base = {
    autofocus: props.autofocus,
    greedy: props.greedy,
    noErrorFocus: props.noErrorFocus,
    noResetFocus: props.noResetFocus,
    ...attrs,
  };
  return hasTheme.value ? base : { ...props.legacyProps, ...base };
});

function validate(shouldFocus) {
  return formRef.value?.validate(shouldFocus);
}

function resetValidation() {
  return formRef.value?.resetValidation();
}

function submit() {
  return formRef.value?.submit();
}

function reset() {
  return formRef.value?.reset();
}

defineExpose({ validate, resetValidation, submit, reset });
</script>

<template>
  <q-form
    ref="formRef"
    v-bind="effectiveProps"
    @submit="emit('submit', $event)"
    @validation-error="emit('validationError', $event)"
    @validation-success="emit('validationSuccess', $event)"
  >
    <!-- @slot default Form field components (HrTextField, HrSelect, etc.). -->
    <slot />
  </q-form>
</template>
